import { useEffect, useRef, useState } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQueryClient } from '@tanstack/react-query'
import Layout from './components/shared/Layout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import OnboardingGuard from './components/auth/OnboardingGuard'
import { ProjectProvider } from './contexts/ProjectContext'
import { GenerationProvider } from './contexts/GenerationContext'
import { useUserRole } from './hooks/useUserRole'
import { setAccessTokenGetter } from './services/api'
import { clearAuth0Storage, tryTriggerSessionRecovery } from './utils/auth0Recovery'

// Pages
import LoginPage from './pages/LoginPage'
import OnboardingPage from './pages/OnboardingPage'
import OnboardingPlanSelectionPage from './pages/OnboardingPlanSelectionPage'
import DashboardPage from './pages/DashboardPage'
import ProjectsPage from './pages/ProjectsPage'
import DocumentsPage from './pages/DocumentsPage'
import DocumentDetailPage from './pages/DocumentDetailPage'
import TemplatesPage from './pages/TemplatesPage'
import OpinionGeneratorPage from './pages/OpinionGeneratorPage'
import SettingsPage from './pages/SettingsPage'
import ProjectDetailPage from './pages/ProjectDetailPage'
import PricingPage from './pages/PricingPage'
import RegisterPage from './pages/RegisterPage'
import AcceptTermsPage from './pages/AcceptTermsPage'
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage'
import SubscriptionFailedPage from './pages/SubscriptionFailedPage'

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminUsersPage from './pages/admin/UsersPage'
import AdminSubscriptionsPage from './pages/admin/SubscriptionsPage'
import AdminAuditPage from './pages/admin/AuditPage'
import AdminFeedbackPage from './pages/admin/FeedbackPage'
import AdminOrganizationsPage from './pages/admin/OrganizationsPage'
import AdminErrorsPage from './pages/admin/ErrorsPage'
import AdminDiscountCodesPage from './pages/admin/DiscountCodesPage'
import AdminRepresentativesPage from './pages/admin/RepresentativesPage'
import AdminRepresentativeDetailPage from './pages/admin/RepresentativeDetailPage'

function App() {
  const { isAdmin, isLoading, isAuthenticated } = useUserRole()
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0()
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()

  // Track if we've already done the initial redirect
  const hasRedirected = useRef(false)
  // Track if the token getter is ready
  const [isApiReady, setIsApiReady] = useState(false)

  // Clear stale query cache and token getter when user logs out
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      queryClient.clear()
      setAccessTokenGetter(null)
      setIsApiReady(false)
    }
  }, [isAuthenticated, isLoading, queryClient])

  // Set up the access token getter for API calls.
  // If Auth0 refresh fails (stale/revoked/rotated refresh token, tenant config change,
  // expired session), the SDK throws. Previously this left users stuck on a generic
  // "Błąd serwera" screen because the interceptor rejected before any network call.
  // We now detect dead-session errors, purge Auth0 storage, and trigger a fresh
  // login automatically — so affected browsers self-heal on next page load.
  useEffect(() => {
    if (isAuthenticated) {
      setAccessTokenGetter(async () => {
        try {
          return await getAccessTokenSilently({
            authorizationParams: {
              audience: import.meta.env.VITE_AUTH0_AUDIENCE || 'LexTool',
            },
          })
        } catch (err: unknown) {
          // Any silent-token failure means we cannot produce a valid access
          // token for this session — whether the SDK labels it
          // login_required / consent_required / invalid_grant /
          // missing_refresh_token / invalid_refresh_token or something else.
          // Treat them all as a dead session: purge only Auth0 SDK state
          // (keeping unrelated persisted UI state intact) and trigger one
          // interactive login redirect even if many concurrent requests hit
          // this at once.
          tryTriggerSessionRecovery(() => {
            clearAuth0Storage()
            void loginWithRedirect({
              appState: { returnTo: window.location.pathname + window.location.search },
            })
          })
          throw err
        }
      })
    }
    setIsApiReady(true)
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect])

  // Redirect admin users to /admin on initial login
  useEffect(() => {
    // Only redirect once, on initial auth load, when user is at root
    if (
      !isLoading &&
      isAuthenticated &&
      isAdmin &&
      location.pathname === '/' &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true
      navigate('/admin', { replace: true })
    }
  }, [isLoading, isAuthenticated, isAdmin, location.pathname, navigate])

  // Show loading while auth is loading OR while setting up API for authenticated users
  if (isLoading || (isAuthenticated && !isApiReady)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Public routes (no auth required) */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      <Route path="/subscription/failed" element={<SubscriptionFailedPage />} />

      {/* Onboarding routes - protected but no onboarding guard */}
      <Route element={<ProtectedRoute />}>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/onboarding/select-plan" element={<OnboardingPlanSelectionPage />} />
        <Route path="/accept-terms" element={<AcceptTermsPage />} />
      </Route>

      {/* Main app routes - with onboarding guard */}
      <Route element={<ProtectedRoute />}>
        <Route
          element={
            <OnboardingGuard>
              <ProjectProvider>
                <GenerationProvider>
                  <Layout />
                </GenerationProvider>
              </ProjectProvider>
            </OnboardingGuard>
          }
        >
          {/* User routes */}
          <Route path="/" element={<DashboardPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/documents/:id" element={<DocumentDetailPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/generate" element={<OpinionGeneratorPage />} />
          <Route path="/settings" element={<SettingsPage />} />

          {/* Admin routes - nested under AdminDashboard */}
          <Route path="/admin" element={<AdminDashboard />}>
            <Route index element={null} />
            <Route path="organizations" element={<AdminOrganizationsPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
            <Route path="discount-codes" element={<AdminDiscountCodesPage />} />
            <Route path="representatives" element={<AdminRepresentativesPage />} />
            <Route path="representatives/:id" element={<AdminRepresentativeDetailPage />} />
            <Route path="errors" element={<AdminErrorsPage />} />
            <Route path="feedback" element={<AdminFeedbackPage />} />
          </Route>
        </Route>
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
